// ======================================================
// System Fingerprint
// ======================================================

function getSystemFingerprint() {
  return {
    ua: navigator.userAgent,
    lang: navigator.language,
    platform: navigator.platform,
    dpr: window.devicePixelRatio,
    width: window.screen.width,
    height: window.screen.height,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    color: window.screen.colorDepth,
    mem: navigator.deviceMemory || null,
    cpu: navigator.hardwareConcurrency || null,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    tzOffset: new Date().getTimezoneOffset(),
    vendor: navigator.vendor,
    product: navigator.product,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}

// ======================================================
// WebGL Fingerprint
// ======================================================

function getWebGLFingerprint() {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");

    if (!gl) return { vendor: "unknown", renderer: "unknown" };

    const ext = gl.getExtension("WEBGL_debug_renderer_info");

    const vendor = ext
      ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);

    const renderer = ext
      ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);

    return { vendor, renderer };
  } catch {
    return { vendor: "unknown", renderer: "unknown" };
  }
}

// ======================================================
// Canvas Fingerprint (усиленный)
// ======================================================

function getCanvasFingerprintAdvanced() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");

    // фон
    ctx.fillStyle = "#f9f9f9";
    ctx.fillRect(0, 0, 200, 60);

    // текст 1
    ctx.textBaseline = "top";
    ctx.font = "16px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillText("enterprise-fp-1", 2, 2);

    // текст 2 с другой гарнитурой и цветом
    ctx.font = "18px 'Times New Roman'";
    ctx.fillStyle = "#069";
    ctx.fillText("enterprise-fp-2", 2, 24);

    // лёгкий stroke
    ctx.strokeStyle = "#888";
    ctx.strokeRect(0, 0, 199, 59);

    return canvas.toDataURL();
  } catch {
    return "canvas_fp_unavailable";
  }
}

// ======================================================
// Audio Fingerprint
// ======================================================

async function getAudioFingerprint() {
  try {
    const AC =
      window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!AC) return "audio_fp_unavailable";

    const ctx = new AC(1, 44100, 44100);

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = 10000;

    const comp = ctx.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ctx.destination);

    osc.start(0);

    const buffer = await ctx.startRendering();
    const data = buffer.getChannelData(0);

    let sum = 0;
    for (let i = 0; i < data.length; i += 100) {
      sum += Math.abs(data[i]);
    }

    return sum.toString();
  } catch {
    return "audio_fp_unavailable";
  }
}

// ======================================================
// Browser Features Fingerprint
// ======================================================

function getBrowserFeaturesFingerprint() {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("webgl2");

  return {
    touch: navigator.maxTouchPoints,
    wasm: typeof WebAssembly === "object",
    webgl2: !!ctx,
    cookies: navigator.cookieEnabled,
    ls: !!window.localStorage,
    ss: !!window.sessionStorage,
    historyLength: window.history.length,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
  };
}

// ======================================================
// WebGPU Fingerprint (если доступен)
// ======================================================

async function getWebGpuFingerprint() {
  try {
    if (!("gpu" in navigator)) {
      return "webgpu_unavailable";
    }

    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) return "webgpu_no_adapter";

    const info = await adapter.requestAdapterInfo
      ? await adapter.requestAdapterInfo()
      : { vendor: "unknown", architecture: "unknown", device: "unknown", description: "unknown" };

    return JSON.stringify(info);
  } catch {
    return "webgpu_error";
  }
}

// ======================================================
// Fonts Fingerprint (упрощённый)
// ======================================================

function getFontsFingerprint() {
  try {
    const baseFonts = ["monospace", "sans-serif", "serif"];
    const testFonts = [
      "Arial",
      "Helvetica",
      "Times New Roman",
      "Courier New",
      "Georgia",
      "Verdana",
      "Tahoma",
      "Trebuchet MS",
    ];

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "fonts_fp_unavailable";

    const results = [];

    function getTextWidth(font) {
      ctx.font = `16px ${font}`;
      return ctx.measureText("abcdefghijkABCDEFGHIJK123456").width;
    }

    const baseWidths = {};
    baseFonts.forEach((bf) => {
      baseWidths[bf] = getTextWidth(bf);
    });

    testFonts.forEach((font) => {
      const widths = baseFonts.map((bf) => getTextWidth(`"${font}", ${bf}`));
      results.push({ font, widths });
    });

    return JSON.stringify(results);
  } catch {
    return "fonts_fp_unavailable";
  }
}

// ======================================================
// SHA-256
// ======================================================

async function sha256(str) {
  const buf = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ======================================================
//  userIdentityEnterprise.js (v4.0)
//  No IP, No URL, No time limit
//  Maximum-strength device fingerprint → stable userId
// ======================================================

export default async() => {
  const fpSystem  = getSystemFingerprint();
  const fpCanvas  = getCanvasFingerprintAdvanced();
  const fpWebGL   = getWebGLFingerprint();
  const fpAudio   = await getAudioFingerprint();
  const fpBrowser = getBrowserFeaturesFingerprint();
  const fpWebGPU  = await getWebGpuFingerprint();
  const fpFonts   = getFontsFingerprint();


  const raw = JSON.stringify({
    sys: fpSystem,
    can: fpCanvas,
    webgl: fpWebGL,
    aud: fpAudio,
    br: fpBrowser,
    wgpu: fpWebGPU,
    fonts: fpFonts,
  });

  return await sha256(raw);
}